import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { SOLICITUD_ALTERNATIVAS_API } from '../../../shared/services/solicitudAlternativas';
import InfoField from '../InfoField';
import ActionButton from '../ActionButton';
import NotasInternasList from '../NotasInternasList';
import ComentariosHistorialList from '../ComentariosHistorialList';
import DecisionAtClienteBanner from './DecisionAtClienteBanner';
import LoadingInline from '../layout/LoadingInline';
import { ModalSection, ModalPanel } from '../layout/ModalSection';
import PiezasList from '../PiezasList';
import { TOAST_FINANZAS_DETALLE_LOAD_ERROR } from '../../utils';

const DetalleFinanzasView = ({
    currentItem,
    onClose,
    onSolicitudUpdated,
}) => {
    const [submitting, setSubmitting] = useState(false);
    const [detalleData, setDetalleData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const id = currentItem?.id;
        if (id == null) {
            setDetalleData(null);
            setLoading(false);
            return undefined;
        }
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await SOLICITUD_ALTERNATIVAS_API.getDetalleFinanzas(
                    id,
                );
                if (cancelled) return;
                if (res?.success) {
                    setDetalleData(res.data ?? null);
                } else {
                    toast.error(
                        res?.message || TOAST_FINANZAS_DETALLE_LOAD_ERROR,
                    );
                    setDetalleData(null);
                }
            } catch (e) {
                if (!cancelled) {
                    toast.error(
                        e?.response?.data?.message ||
                            e?.message ||
                            TOAST_FINANZAS_DETALLE_LOAD_ERROR,
                    );
                    setDetalleData(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [currentItem?.id]);

    const resumen = detalleData?.resumen;
    const notasInternas = detalleData?.notas_internas || [];
    const comentarios = detalleData?.comentarios_historial || [];

    const handleFinalizar = useCallback(async () => {
        setSubmitting(true);
        try {
            const res = await SOLICITUD_ALTERNATIVAS_API.finalizarGestionFinanzas(
                currentItem.id,
            );
            if (!res?.success) {
                toast.error(
                    res?.message || 'No se ha podido finalizar la gestión.',
                );
                return;
            }
            toast.success('Gestión finalizada correctamente.');
            onSolicitudUpdated?.(res.data);
            onClose();
        } catch (e) {
            toast.error(
                e?.response?.data?.message ||
                    e?.message ||
                    'Error al finalizar la gestión.',
            );
        } finally {
            setSubmitting(false);
        }
    }, [currentItem.id, onClose, onSolicitudUpdated]);

    if (loading) {
        return <LoadingInline />;
    }

    const hasPiezasOrigen = resumen?.piezas_origen?.length > 0;
    const hasPiezasAlternativa = resumen?.piezas_alternativa?.length > 0;
    const piezasComparacion =
        hasPiezasOrigen || hasPiezasAlternativa;
    const piezasColClass =
        hasPiezasOrigen && hasPiezasAlternativa ? 'col-md-6 mt-3' : 'col-12 mt-3';

    return (
        <div>
            <DecisionAtClienteBanner decision={detalleData?.decision} />

            <ModalSection title="Comentarios">
                <ModalPanel>
                    <ComentariosHistorialList
                        comentarios={comentarios}
                    />
                </ModalPanel>
            </ModalSection>

            <ModalSection title="Resumen de la operación">
                <ModalPanel>
                    <div className="row g-5">
                        <div className="col-md-6">
                            <InfoField
                                label="Nº Presupuesto origen"
                                value={resumen?.presupuesto_origen || '-'}
                            />
                        </div>
                        <div className="col-md-6">
                            <InfoField
                                label="Nº Presupuesto alternativa"
                                value={
                                    resumen?.presupuesto_alternativa || '-'
                                }
                            />
                        </div>
                        <div className="col-md-6 mt-3">
                            <InfoField
                                label="Asignado at. cliente"
                                value={resumen?.at_cliente || '-'}
                                className="text-capitalize"
                            />
                        </div>
                        <div className="col-md-6 mt-3">
                            <InfoField
                                label="Cliente"
                                className="d-flex flex-column gap-1"
                                value={
                                    <>
                                        <span>
                                            {resumen?.cliente?.nombre || '-'}
                                        </span>
                                        <span>
                                            {resumen?.cliente?.telefono || '-'}
                                        </span>
                                    </>
                                }
                            />
                        </div>
                        {piezasComparacion && (
                            <div className="col-12">
                                <div className="row g-5">
                                    {hasPiezasOrigen && (
                                        <div className={piezasColClass}>
                                            <InfoField
                                                label="Piezas origen"
                                                value={
                                                    <PiezasList
                                                        piezas={
                                                            resumen.piezas_origen
                                                        }
                                                        variant="origen"
                                                        precioVentaTotal={
                                                            resumen.precio_venta_origen
                                                        }
                                                    />
                                                }
                                            />
                                        </div>
                                    )}
                                    {hasPiezasAlternativa && (
                                        <div className={piezasColClass}>
                                            <InfoField
                                                label="Piezas alternativa"
                                                value={
                                                    <PiezasList
                                                        piezas={
                                                            resumen.piezas_alternativa
                                                        }
                                                        variant="origen"
                                                        precioVentaTotal={
                                                            resumen.precio_venta_alternativa
                                                        }
                                                    />
                                                }
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </ModalPanel>
            </ModalSection>

            <ModalSection title="Notas internas">
                <ModalPanel>
                    <NotasInternasList notas={notasInternas} />
                </ModalPanel>
            </ModalSection>

            <div
                className="p-3 mb-4 rounded"
                style={{
                    backgroundColor: '#fff3cd',
                    color: '#856404',
                    fontSize: '13px',
                    lineHeight: 1.55,
                }}
            >
                Una vez se finalice la gestión, se notificará al recambista y al
                responsable de atención al cliente, y el estado pasará a{' '}
                <span className="fw-semibold">Finalizada</span> automáticamente.
            </div>

            <div className="d-flex justify-content-end">
                <ActionButton
                    disabled={submitting}
                    loading={submitting}
                    label="Finalizar gestión"
                    onClick={handleFinalizar}
                />
            </div>
        </div>
    );
};

export default DetalleFinanzasView;
